// Lease identification mode (S4, contract section 8).
//
// A customer who opens the identification link from a lease email (m.24rent.fi/profile?lease=<token>) runs profile
// completeness in a "lease visit". Two sessionStorage keys, deliberately separate:
//
//  - r24.leaseKyc    the lease token. The bearer for lease requests only. Cleared as soon as the lease is over
//                    (done, cancelled, expired, not this account).
//  - r24.leaseVisit  the visit guard. Says "this browser session is a lease visit". Clearing the token NEVER clears
//                    the guard: if lease mode ended with the token, the same page would fall back to the ordinary
//                    identification flow at once, whose Traficom checks announce to Hansel.
//
// The guard starts 'unbound' (the link may be opened logged out), is bound to the customer id at the first
// authenticated resolve and is discarded when authentication resolves to another customer: authentication lives in
// localStorage and is shared by all tabs, the guard lives in one tab's sessionStorage.
//
// Leaving a visit (the terminal view's control, or logout) sets an in-memory latch BEFORE the keys are removed.
// window.location.assign() does not destroy the document synchronously, so an answer that was pending can still
// resolve in it; the latch keeps every request site closed until the document is gone. The latch is never stored
// and never cleared.
//
// Everything outside a lease visit is untouched: when leaseVisit() is false no code path in the app changes.

export const LEASE_TOKEN_KEY = 'r24.leaseKyc';
export const LEASE_VISIT_KEY = 'r24.leaseVisit';
export const LEASE_STATUS_KEY = 'r24.leaseStatus';
export const LEASE_PCRID_KEY = 'pcRID';
export const LEASE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22}$/;

export type LeaseTerminalStatus = 'done' | 'cancelled' | 'expired' | 'invalid' | 'not_ready';

export type LeaseFailure =
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'closed'; status?: string }
  | { kind: 'network' };

let leaving = false;
let lastTerminalStatus: LeaseTerminalStatus | null = null;

const readKey = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeKey = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // storage unavailable: the visit simply cannot be kept
  }
};

const removeKey = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    // nothing to remove
  }
};

/** The document-local leaving latch. */
export const leaseLeaving = (): boolean => leaving;

/** Lease mode: a visit guard is stored, or this document is leaving one. Not defined by the token. */
export const leaseVisit = (): boolean => leaving || readKey(LEASE_VISIT_KEY) !== null;

/** The lease token, if one is held and well formed. Always null once the document is leaving. */
export const leaseToken = (): string | null => {
  if (leaving) {
    return null;
  }
  const token = readKey(LEASE_TOKEN_KEY);
  return token !== null && LEASE_TOKEN_PATTERN.test(token) ? token : null;
};

/** Drops the token. The visit guard stays: the terminal lease view follows. */
export const clearLeaseToken = (): void => removeKey(LEASE_TOKEN_KEY);

/**
 * The lease is over: remember what the terminal view should say (kept in sessionStorage so a reload shows the
 * same text), and drop the token. The guard stays.
 */
export const endLeaseWith = (status: LeaseTerminalStatus): void => {
  lastTerminalStatus = status;
  writeKey(LEASE_STATUS_KEY, status);
  clearLeaseToken();
};

/** What the terminal view says. Without a recorded status it is the cancelled text (contact customer service). */
export const leaseTerminalStatus = (): LeaseTerminalStatus => {
  if (lastTerminalStatus !== null) {
    return lastTerminalStatus;
  }
  const stored = readKey(LEASE_STATUS_KEY);
  return stored === 'done' || stored === 'cancelled' || stored === 'expired' || stored === 'invalid' || stored === 'not_ready'
    ? stored
    : 'cancelled';
};

const removeVisitKeys = (): void => {
  removeKey(LEASE_VISIT_KEY);
  removeKey(LEASE_TOKEN_KEY);
  removeKey(LEASE_STATUS_KEY);
  removeKey(LEASE_PCRID_KEY);
};

/**
 * Leaves the visit. Order matters and is part of the contract: latch first, then the keys, then a full document
 * load. Logout in a lease visit ends here too.
 */
export const leaveLeaseVisit = (): void => {
  leaving = true;
  removeVisitKeys();
  window.location.assign('/');
};

/**
 * Called when the app learns which customer is authenticated. Binds an unbound visit to that customer, keeps a visit
 * that already belongs to them, and discards a visit that belongs to someone else (that customer gets the ordinary
 * app: no latch, no navigation).
 */
export const resolveLeaseVisitFor = (
  customerId: number | string | undefined | null
): 'none' | 'bound' | 'kept' | 'discarded' => {
  if (leaving || customerId === undefined || customerId === null) {
    return 'none';
  }
  const guard = readKey(LEASE_VISIT_KEY);
  if (guard === null) {
    return 'none';
  }
  const id = String(customerId);
  if (guard === 'unbound') {
    writeKey(LEASE_VISIT_KEY, id);
    return 'bound';
  }
  if (guard === id) {
    return 'kept';
  }
  lastTerminalStatus = null;
  removeVisitKeys();
  return 'discarded';
};

/** Splits a query string into its raw parts and the raw values of the `lease` parameter. */
export const splitLeaseParam = (query: string): { rest: string[]; lease: string[] } => {
  const rest: string[] = [];
  const lease: string[] = [];
  query.split('&').forEach((part) => {
    if (part === '') {
      return;
    }
    const eq = part.indexOf('=');
    const rawKey = eq === -1 ? part : part.slice(0, eq);
    let key = rawKey;
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
    } catch (e) {
      key = rawKey;
    }
    if (key === 'lease') {
      lease.push(eq === -1 ? '' : part.slice(eq + 1));
    } else {
      rest.push(part);
    }
  });
  return { rest, lease };
};

const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

// The backend's refusals are JSend `fail` answers whose `data` is {error, status?} (HttpFailure.body is that `data`).
const failureCode = (body: any): string | undefined => (typeof body?.error === 'string' ? body.error : undefined);

const failureStatus = (body: any): string | undefined => (typeof body?.status === 'string' ? body.status : undefined);

/**
 * Reads what a failed lease request means. HTTP failures of the three lease requests carry the status and the body
 * (see HttpFailure); anything else is a network failure and keeps the token.
 *  404 / 403           not this account's lease (the uniform "lease_link_invalid")
 *  409 link expired    the expired text
 *  409 closed          the text of the lease's status
 *  503 and the rest    "could not load right now", the token is kept
 */
export const classifyLeaseFailure = (err: any): LeaseFailure => {
  const status = asNumber(err?.status);
  if (status !== undefined) {
    if (status === 404 || status === 403) {
      return { kind: 'invalid' };
    }
    if (status === 409) {
      const code = failureCode(err?.body);
      if (code === 'lease_link_expired') {
        return { kind: 'expired' };
      }
      return { kind: 'closed', status: failureStatus(err?.body) };
    }
    return { kind: 'network' };
  }
  return { kind: 'network' };
};

/** Maps a lease status (from the lookup or from a 409's body) to the terminal view it ends in, if it is terminal. */
export const terminalStatusFor = (status: string | undefined): LeaseTerminalStatus | null => {
  switch (status) {
    case 'done':
    case 'cancelled':
    case 'expired':
    case 'not_ready':
      return status;
    default:
      return null;
  }
};

/** Test support only: the latch is module state and survives between tests of one file. Production never calls this. */
export const __testOnly = {
  resetLeavingLatch: (): void => {
    leaving = false;
    lastTerminalStatus = null;
  },
};
